/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {Entity, EntityStatus} from '@ion/core/src/types/entities';
import Dropdown, {Options} from '../components/menus/Dropdown';

export type EntityStatusChooserProps = {
  entity?: Entity;
  status?: EntityStatus;
  includeDeleted?: boolean;
  readOnly?: boolean;
  onChange?: (status: EntityStatus, label: string) => void;
  required?: boolean;
  error?: string;
  classes?: string;
  defaultValue?: string;
  defaultLabel?: boolean;
  name?: string;
  isRoleViewMode?: boolean;
}

/**
 * Generates the entity status chooser dropdown labels and values based on
 * the translated status entries.
 */
export const getOptions = (includeDeleted: boolean): Options[] => {
  const entries  = Object.entries(translate('common:entityStatus', {returnObjects: true}));
  const statuses = entries.map(([value, label]) => ({value, label})) as unknown as Options[];
  return !includeDeleted ? statuses.filter(({value}) => {
    return value !== EntityStatus.Deleted;
  }) : statuses;
};

/**
 * @EntityStatusChooser
 *
 * The EntityStatusChooser provides a list of localized CTC Entity Status values
 * (such as “Enabled”, “Disabled”, etc.) for display within a dropdown list and
 * allows the user to select a Status from the list which will be returned to the
 * client as the non-translated CTC Entity Status value.
 *
 * ```typescript
 *
 *     import {EntityStatusChooser} from '@ion/dls/src/editors/EntityStatusChooser';
 *     import {Entity} from '@ion/core/src/types/entities';
 *
 *     <EntityStatusChooser status={item.status} onChange(setStatus) />
 *
 *     // read-only mode ...
 *     <EntityStatusChooser readOnly
 *       entity={Entity.asset}
 *       status={item.status}
 *       onChange(setStatus)
 *       />
 *
 *     // include deleted status (excluded by default, generally not exposed) ...
 *     <EntityStatusChooser includeDelete
 *       status={item.status}
 *       onChange(setStatus)
 *       />
 *
 *     // provide an entity specific inline label ...
 *     <EntityStatusChooser entity={Entity.asset} onChange(setStatus) />
 *
 *
 * @status        Allows for providing a default EntityStatus, generally used for
 *                edits.
 * @onChange      Callback prop which will be invoked with the seleted CTC entity
 *                status value and selected label.
 * @entity        The context of the EntityStatus chooser; i.e. the CTC Entity type
 *                for which the chooser is being displayed (used for determining the
 *                label to be displayed). If omitted, the inline label will not be
 *                displayed.
 * @defaultLabel  When true, default label "Status" will be displayed when entity is
 *                not defined. Defaults to false.
 * @readOnly      When true, the component will be displayed in read-only mode;
 *                that is, disabled. Defaults to false.
 * @includeDelete When true, the Deleted status will be included in the list of
 *                statuses. Defaults to false.
 */
export const EntityStatusChooser = ({
  entity,
  status,
  includeDeleted,
  readOnly,
  onChange,
  required,
  classes,
  name,
  isRoleViewMode,
  defaultLabel = false
}: EntityStatusChooserProps) => {
  // A default value is often set, we should
  // onChange if one exists on mount so the
  // consumer's formState can be updated if
  // need be.
  useEffect(() => {
    if (status) {
      onChange?.(status, name);
    }
  }, []);

  return (
    <div className={classes}>
      <Dropdown required={required}
        displaySelectedLabel
        disabled={readOnly || isRoleViewMode}
        value={status}
        options={getOptions(includeDeleted)}
        placeholder={translate('common:editors.entityStatus.placeholder')}
        fieldLabel={entity ? translate('common:editors.entityStatus.label', {
          entity : translate(`common:entityLabels.${entity.toLowerCase()}`)
        }) : defaultLabel ? translate('common:editors.status.label') : undefined}
        onChange={onChange ? function({target}) {
          onChange(target.value as EntityStatus, target.name || name);
        } : null} />
    </div>
  );
};
export default EntityStatusChooser;
